import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Button,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Contacts as ContactsIcon,
  Receipt as ReceiptIcon,
  AccountBalance as BankingIcon,
  Description as DocumentsIcon,
  Assessment as VATIcon,
  BarChart as ReportsIcon,
  Task as TasksIcon,
  Message as MessagesIcon,
  Event as CalendarIcon,
  Settings as SettingsIcon,
  ExpandLess,
  ExpandMore,
  NoteAdd,
  CreditCard,
  ArrowBack,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const drawerWidth = 240;

const AccountantSideNav = ({ mobileOpen, onMobileClose, onSwitchToClientView }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openInvoices, setOpenInvoices] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [notificationVisible, setNotificationVisible] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    const handleNotificationVisibility = (e) => setNotificationVisible(e.detail.isVisible);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('offlineNotificationVisibility', handleNotificationVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('offlineNotificationVisibility', handleNotificationVisibility);
    };
  }, []);

  const handleInvoicesClick = () => {
    setOpenInvoices(!openInvoices);
  };

  const handleNavClick = () => {
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const menuItems = [
    { text: t('accountant_dashboard'), icon: <DashboardIcon />, path: '/accountant-portal/dashboard' },
    { text: t('accountant_clients'), icon: <ContactsIcon />, path: '/accountant-portal/clients' },
    {
      text: t('accountant_invoices'),
      icon: <ReceiptIcon />,
      hasSubmenu: true,
      subItems: [
        { text: t('incoming_invoices'), icon: <NoteAdd />, path: '/accountant-portal/invoices/incoming' },
        { text: t('outgoing_invoices'), icon: <ReceiptIcon />, path: '/accountant-portal/invoices/outgoing' },
        { text: t('credit_notes'), icon: <CreditCard />, path: '/accountant-portal/invoices/credit-notes' },
      ],
    },
    { text: t('accountant_banking'), icon: <BankingIcon />, path: '/accountant-portal/banking' },
    { text: t('accountant_documents'), icon: <DocumentsIcon />, path: '/accountant-portal/documents' },
    { text: t('accountant_vat_taxes'), icon: <VATIcon />, path: '/accountant-portal/vat-taxes' },
    { text: t('accountant_reports'), icon: <ReportsIcon />, path: '/accountant-portal/reports' },
    { text: t('accountant_tasks'), icon: <TasksIcon />, path: '/accountant-portal/tasks' },
    { text: t('accountant_messages'), icon: <MessagesIcon />, path: '/accountant-portal/messages' },
    { text: t('accountant_calendar'), icon: <CalendarIcon />, path: '/accountant-portal/calendar' },
    { text: t('accountant_settings'), icon: <SettingsIcon />, path: '/accountant-portal/settings' },
  ];

  const drawer = (
    <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#667eea' }}>
          {t('accountant_portal')}
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={onSwitchToClientView}
          sx={{
            textTransform: 'none',
            borderRadius: '8px',
            borderColor: '#667eea',
            color: '#667eea',
            '&:hover': {
              borderColor: '#5568d3',
              backgroundColor: 'rgba(102, 126, 234, 0.04)',
            },
          }}
        >
          {t('switch_to_client_view')}
        </Button>
      </Box>

      <Divider />

      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {item.hasSubmenu ? (
              <>
                <ListItem
                  button
                  onClick={handleInvoicesClick}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <ListItemIcon sx={{ color: '#667eea' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                  {openInvoices ? <ExpandLess /> : <ExpandMore />}
                </ListItem>
                <Collapse in={openInvoices} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem
                        button
                        key={subItem.text}
                        component={Link}
                        to={subItem.path}
                        onClick={handleNavClick}
                        selected={location.pathname === subItem.path}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(102, 126, 234, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(102, 126, 234, 0.15)',
                            },
                          },
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: '#667eea', minWidth: 40 }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.text} />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </>
            ) : (
              <ListItem
                button
                component={Link}
                to={item.path}
                onClick={handleNavClick}
                selected={location.pathname === item.path}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.15)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
              >
                <ListItemIcon sx={{ color: '#667eea' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
    >
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onMobileClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              top: notificationVisible ? { xs: 70 + 28, sm: 70 + 22 } : 70,
              height: notificationVisible ? { xs: 'calc(100% - 98px)', sm: 'calc(100% - 92px)' } : 'calc(100% - 70px)',
              transition: 'top 0.3s ease-in-out, height 0.3s ease-in-out',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      )}
    </Box>
  );
};

export default AccountantSideNav;
